import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {object} RewardTier
 * @property {number} productId
 * @property {number} variantId
 * @property {number} thresholdCents
 */

/**
 * @typedef {object} RewardsConfig
 * @property {RewardTier[]} rewards
 * @property {number} [multiplierProductId]
 * @property {number} [multiplierVariantId]
 */

/**
 * @typedef {object} AjaxCartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number} [final_line_price]
 * @property {number} [line_price]
 */

/**
 * @typedef {object} AjaxCart
 * @property {AjaxCartItem[]} items
 * @property {Record<string, string>} [sections]
 */

const SOURCE = 'cart-entries-rewards';

/** @type {RewardsConfig} */
const CONFIG = readConfig();
/** @type {RewardTier[]} */
const REWARDS = CONFIG.rewards ?? [];
const MULTIPLIER_PRODUCT_ID = Number(CONFIG.multiplierProductId) || 0;
const MULTIPLIER_VARIANT_ID = Number(CONFIG.multiplierVariantId) || 0;

/** @type {boolean} */
let syncing = false;

function readConfig() {
  const configEl = document.getElementById('cart-entries-rewards-config');
  if (!(configEl instanceof HTMLScriptElement) || !configEl.textContent) return { rewards: [] };

  try {
    return /** @type {RewardsConfig} */ (JSON.parse(configEl.textContent));
  } catch (error) {
    console.warn('[cart-entries-rewards] Invalid config:', error);
    return { rewards: [] };
  }
}

/**
 * @returns {string}
 */
function sectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  }
  return [...new Set(ids)].join(',');
}

/**
 * @returns {string}
 */
function cartJsonUrl() {
  return `${Theme.routes.cart_url}.json`;
}

/**
 * @returns {string}
 */
function cartUpdateUrl() {
  const url = Theme.routes.cart_update_url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * @param {AjaxCartItem[]} items
 * @returns {number}
 */
function qualifyingSubtotal(items) {
  const rewardIds = new Set(REWARDS.map((tier) => tier.productId));
  return items.reduce((sum, item) => {
    if (rewardIds.has(item.product_id) || item.product_id === MULTIPLIER_PRODUCT_ID) return sum;
    return sum + (item.final_line_price ?? item.line_price ?? 0);
  }, 0);
}

/**
 * @param {AjaxCartItem[]} items
 * @returns {boolean}
 */
function hasQualifyingProducts(items) {
  const rewardIds = new Set(REWARDS.map((tier) => tier.productId));
  return items.some((item) => {
    if ((item.quantity ?? 0) <= 0) return false;
    if (rewardIds.has(item.product_id) || item.product_id === MULTIPLIER_PRODUCT_ID) return false;
    return true;
  });
}

/**
 * @returns {Promise<AjaxCart>}
 */
async function fetchCart() {
  const response = await fetch(cartJsonUrl(), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * @param {Record<string, number>} updates
 * @returns {Promise<AjaxCart>}
 */
async function updateCart(updates) {
  const body = JSON.stringify({
    updates,
    sections: sectionIds(),
    sections_url: window.location.pathname,
  });

  const response = await fetch(cartUpdateUrl(), fetchConfig('json', { body }));
  if (!response.ok) throw new Error(`Failed to update cart: ${response.status}`);
  return response.json();
}

/**
 * @param {AjaxCart} data
 * @param {Record<string, number>} updates
 */
function dispatchCartUpdate(data, updates) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
    detail: {
      sections: data.sections,
      items: data.items,
      source: SOURCE,
      didError: false,
    },
  });
}

/**
 * @param {AjaxCartItem[] | undefined} [eventItems]
 */
async function syncRewards(eventItems) {
  if (syncing || REWARDS.length === 0) return;
  syncing = true;

  try {
    const items = eventItems ?? (await fetchCart()).items ?? [];
    const subtotal = qualifyingSubtotal(items);
    /** @type {Record<number, number>} */
    const quantities = {};
    for (const item of items) {
      quantities[item.variant_id] = (quantities[item.variant_id] ?? 0) + item.quantity;
    }

    /** @type {Record<string, number>} */
    const updates = {};
    let changed = false;

    for (const tier of REWARDS) {
      const wanted = subtotal >= tier.thresholdCents ? 1 : 0;
      const current = quantities[tier.variantId] ?? 0;
      if (current !== wanted) {
        updates[String(tier.variantId)] = wanted;
        changed = true;
      }
    }

    const multiplierQty = MULTIPLIER_VARIANT_ID > 0 ? (quantities[MULTIPLIER_VARIANT_ID] ?? 0) : 0;
    if (multiplierQty > 0 && !hasQualifyingProducts(items)) {
      updates[String(MULTIPLIER_VARIANT_ID)] = 0;
      changed = true;
    }

    if (!changed) return;

    const updatedCart = await updateCart(updates);
    dispatchCartUpdate(updatedCart, updates);
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.warn('[cart-entries-rewards] Sync failed:', error);
    }
  } finally {
    syncing = false;
  }
}

/**
 * @param {import('@shopify/events').CartLinesUpdateEvent} event
 */
function handleCartUpdate(event) {
  event.promise
    ?.then(({ detail }) => {
      if (detail?.source === SOURCE) return;
      const items = Array.isArray(detail?.items) ? /** @type {AjaxCartItem[]} */ (detail.items) : undefined;
      return syncRewards(items);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-entries-rewards] Event promise rejected:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);
document.addEventListener(StandardEvents.cartDiscountUpdate, handleCartUpdate);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncRewards();
  });
} else {
  syncRewards();
}
